import React, { Component } from 'react';

import moment from 'moment';

import { IEventDetailedRound } from '../../../../../store/admin/eventRounds/store';
import { IAdminEventActions } from '../../../../../store/admin/events/actions';
import { IAdminEvent } from '../../../../../store/admin/events/store';
import { IAdminRoundFormatsActions } from '../../../../../store/admin/roundFormats/actions';
import {
  IAdminRoundFormatsStore,
  IFormat,
  IMetaFormat
} from '../../../../../store/admin/roundFormats/store';
import { ScoreType } from '../../../../../store/admin/eventFormats/store';
import { INotificationsActions } from '../../../../../store/notification/actions';

import { FormConsumer, FormProvider, IFormAttributes } from '../../../../../components/Forms';
import GeneralRoundInfo from '../../../../../components/GeneralRoundInfo';
import { asyncForEach, lazyEqual } from '../../../../../contrib/utils';
import { sendErrorPushNotification } from '../../../../player/EventHome/Utils/sendErrorPushNotification';
import { sendSuccessPushNotification } from '../../../../player/EventHome/Utils/sendSuccessPushNotification';

interface IProps {
  context: {
    actions: {
      adminEvents: IAdminEventActions;
      adminRoundFormats: IAdminRoundFormatsActions;
      notifications: INotificationsActions;
    };
    adminRoundFormats: IAdminRoundFormatsStore;
    clubId: number;
    event: IAdminEvent;
    onGetRoundFormat: (roundId: number) => void;
    onFormatUpdate: (roundFormatDrafts: IMetaFormat[]) => void;
    onDraftUpdate: (roundId: number, draft: Partial<IEventDetailedRound>) => void;
    selectedRound: number;
    roundDrafts: {
      [roundId: number]: Partial<IEventDetailedRound>;
    };
    setRef: any;
  };
  item: IEventDetailedRound;
}

interface IState {
  roundFormatDrafts: IMetaFormat[];
}

class RoundDetails extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      roundFormatDrafts: []
    };
  }

  public saveRound = (round: IEventDetailedRound) => async () => {
    const {
      clubId,
      event,
      actions: {
        notifications: { push: pushNotification },
        adminEvents: { editRound }
      }
    } = this.props.context;

    const response = await editRound(clubId, event as IAdminEvent, round);

    if (response.errors && response.errors.events) {
      sendErrorPushNotification(pushNotification, response.errors);
    } else {
      sendSuccessPushNotification(
        pushNotification,
        `Round ${round.order + 1} updated successfully.`
      );
    }
  };

  public shouldComponentUpdate(nextProps: IProps, nextState: IState): boolean {
    const {
      context,
      context: { adminRoundFormats, roundDrafts },
      item: { id: itemId }
    } = this.props;

    const {
      context: nextContext,
      context: { adminRoundFormats: nextAdminRoundFormats, roundDrafts: nextRoundDrafts }
    } = nextProps;

    return (
      !lazyEqual(adminRoundFormats, nextAdminRoundFormats, 'masterFormats') ||
      !lazyEqual(adminRoundFormats, nextAdminRoundFormats, String(itemId)) ||
      !lazyEqual(roundDrafts, nextRoundDrafts, String(itemId)) ||
      !lazyEqual(context, nextContext, 'selectedRound') ||
      !lazyEqual(context, nextContext, 'courses') ||
      !lazyEqual(this.props, nextProps, 'item') ||
      !lazyEqual(this.state, nextState)
    );
  }

  public onToggleScore = (formatId: string, scoretype: ScoreType) => async () => {
    const {
      item: round,
      context: {
        actions,
        adminRoundFormats,
        clubId,
        event,
        onFormatUpdate,
        actions: {
          notifications: { push: pushNotification }
        }
      }
    } = this.props;
    const { roundFormatDrafts } = this.state;
    const currentRoundFormats = adminRoundFormats[round.id].concat(roundFormatDrafts);

    const found = currentRoundFormats.find(
      rf => rf.format.id === Number(formatId) && rf.scoreType === scoretype
    );

    if (found) {
      const response = await actions.adminRoundFormats.delete(clubId, event.id, round.id, found.id);

      if (response.errors) {
        sendErrorPushNotification(pushNotification, response.errors);
      } else {
        sendSuccessPushNotification(pushNotification, 'Score type updated.');
      }
    } else {
      const response = await actions.adminRoundFormats.create(clubId, event.id, round.id, ({
        format: Number(formatId),
        round: round.id,
        scoreType: scoretype
      } as any) as Partial<IMetaFormat>);

      if (response.errors) {
        sendErrorPushNotification(pushNotification, response.errors);
      } else {
        sendSuccessPushNotification(pushNotification, 'Score type updated.');

        const drafts = roundFormatDrafts.filter(d => d.format.id !== Number(formatId));
        this.setState({ roundFormatDrafts: drafts }, () =>
          onFormatUpdate(this.state.roundFormatDrafts)
        );
      }
    }
  };

  public onAddDraft = (format: IFormat) => async () => {
    await this.onToggleScore(String(format.id), ScoreType.GROSS)();
    await this.onToggleScore(String(format.id), ScoreType.NET)();
  };

  public onDeleteFormats = (formatId: string) => async () => {
    const {
      item: round,
      context: {
        actions,
        adminRoundFormats,
        clubId,
        event,
        onFormatUpdate,
        actions: {
          notifications: { push: pushNotification }
        }
      }
    } = this.props;
    const { roundFormatDrafts } = this.state;
    const currentRoundFormats = adminRoundFormats[round.id].concat(roundFormatDrafts);

    const found = currentRoundFormats.filter(rf => rf.format.id === Number(formatId));

    await asyncForEach(found, async (foundFormat: Partial<IMetaFormat>) => {
      if (foundFormat.id) {
        const response = await actions.adminRoundFormats.delete(
          clubId,
          event.id,
          round.id,
          foundFormat.id
        );

        if (response.errors) {
          sendErrorPushNotification(pushNotification, response.errors);
        } else {
          sendSuccessPushNotification(pushNotification, 'Format successfully deleted.');
        }
      }

      const drafts = roundFormatDrafts.filter(d => d.format.id !== Number(formatId));
      this.setState({ roundFormatDrafts: drafts }, () => {
        onFormatUpdate(this.state.roundFormatDrafts);
      });
    });
  };

  public render(): JSX.Element {
    const { roundDrafts } = this.props.context;

    const round = { ...this.props.item, ...(roundDrafts[this.props.item.id] || {}) };

    const defaultRound = {
      ...round,
      playersPerTee: round.playersPerTee || 4,
      teeFrequency: !!round.teeFrequency
        ? String(moment.duration(round.teeFrequency).asMinutes())
        : '5'
    };

    return (
      <div className="round-details" key={`round-${round.id}-details`}>
        <FormProvider
          defaultValues={defaultRound}
          onSubmit={this.saveRound}
          validations={{
            start: 'required',
            totalPlayers: 'required',
            totalRounds: 'required'
          }}
          {...{ ref: this.props.context.setRef(round.id) }}
        >
          <FormConsumer>
            {({ values, formActions: { setValue } }: IFormAttributes) => (
              <GeneralRoundInfo
                setValue={setValue}
                values={values}
                roundId={round.id}
                displayHandicapAllowance={false}
              />
            )}
          </FormConsumer>
        </FormProvider>
      </div>
    );
  }
}

export { RoundDetails };
